import { useState, useEffect, useCallback } from "react";
import { jsPDF } from "jspdf";
import {
  CheckCircle, X, Trash2, Download, Eye, Clock, Copy,
  Dumbbell, ShoppingCart, PlusCircle, RefreshCw, Mail, Link, FileText
} from "lucide-react";
import { API } from "./adminConstants";

const STATUS_CONFIG = {
  aanvraag_ontvangen: { label: "Aanvraag ontvangen", cls: "text-yellow-400 bg-yellow-400/10 border-yellow-400/25" },
  betaald:            { label: "Betaald",             cls: "text-blue-400 bg-blue-400/10 border-blue-400/25" },
  intake_ontvangen:   { label: "Intake ontvangen",    cls: "text-purple-400 bg-purple-400/10 border-purple-400/25" },
  schema_gereed:      { label: "Schema gereed",       cls: "text-green-400 bg-green-400/10 border-green-400/25" },
};

function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl text-sm font-semibold shadow-2xl animate-fadeIn
      ${type === "success" ? "bg-[#111] border border-[#C9A84C]/40 text-white" : "bg-red-950 border border-red-500/40 text-red-300"}`}>
      {type === "success" ? <CheckCircle size={16} className="text-[#C9A84C]" /> : <X size={16} className="text-red-400" />}
      {msg}
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100"><X size={14} /></button>
    </div>
  );
}

function SchemaToevoegenModal({ token, userId, orderNaam, onClose, onAssigned, onGoToMaker }) {
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigningId, setAssigningId] = useState(null);
  const [assigned, setAssigned] = useState(false);
  const authHeaders = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetch(`${API}/api/admin/custom-schemas`, { headers: authHeaders })
      .then(r => r.json())
      .then(d => { setSchemas(Array.isArray(d) ? d : (d.schemas || [])); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const assignSchema = async (schemaId) => {
    setAssigningId(schemaId);
    try {
      const res = await fetch(`${API}/api/admin/custom-schemas/${schemaId}/assign`, {
        method: "POST", headers: authHeaders,
        body: JSON.stringify({ user_id: userId }),
      });
      if (res.ok) { setAssigned(true); onAssigned(); }
    } catch {}
    setAssigningId(null);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#111] border border-white/10 rounded-2xl p-7 max-w-lg w-full animate-fadeIn" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-white font-bold text-lg">Schema toewijzen</h3>
            <p className="text-zinc-500 text-xs mt-0.5">Selecteer een custom schema voor <span className="text-white font-semibold">{orderNaam}</span></p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors"><X size={18} /></button>
        </div>

        {assigned ? (
          <div className="text-center py-6 animate-fadeIn">
            <div className="w-16 h-16 rounded-full bg-[#C9A84C]/15 border border-[#C9A84C]/30 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={28} className="text-[#C9A84C]" />
            </div>
            <p className="text-white font-bold text-lg mb-1">Schema toegewezen!</p>
            <p className="text-zinc-500 text-sm mb-5">Klik nu op <strong className="text-white">"Schema gereed"</strong> om de klant te notificeren.</p>
            <button onClick={onClose} className="btn-gold px-6 py-2.5 text-sm font-bold uppercase tracking-wide">Sluiten</button>
          </div>
        ) : loading ? (
          <div className="py-10 text-center text-zinc-500 text-sm">Laden...</div>
        ) : schemas.length === 0 ? (
          <div className="text-center py-8">
            <Dumbbell size={32} className="text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm mb-1 font-semibold">Geen custom schema's gevonden</p>
            <p className="text-zinc-600 text-xs mb-5">Maak eerst een schema aan in de Schema Maker.</p>
            <button onClick={() => { onClose(); onGoToMaker(); }}
              className="btn-gold px-6 py-2.5 text-sm font-bold uppercase tracking-wide flex items-center gap-2 mx-auto">
              <PlusCircle size={14} /> Ga naar Schema Maker
            </button>
          </div>
        ) : (
          <div>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {schemas.map(s => (
                <div key={s.id} data-testid={`schema-option-${s.id}`}
                  className="flex items-center justify-between bg-[#0f0f0f] border border-white/6 rounded-xl px-4 py-3">
                  <div>
                    <p className="text-white font-semibold text-sm">{s.naam || s.name || "Naamloos schema"}</p>
                    <p className="text-zinc-500 text-xs">{[s.doel, s.niveau, s.dagen ? `${s.dagen} dagen` : ""].filter(Boolean).join(" · ")}</p>
                  </div>
                  <button onClick={() => assignSchema(s.id)} disabled={!!assigningId}
                    data-testid={`assign-custom-schema-${s.id}`}
                    className="btn-gold-course px-3 py-1.5 text-xs font-bold uppercase tracking-wide flex items-center gap-1.5 disabled:opacity-60 shrink-0 ml-3">
                    {assigningId === s.id ? "..." : <><CheckCircle size={11} /> Toewijzen</>}
                  </button>
                </div>
              ))}
            </div>
            <button onClick={() => { onClose(); onGoToMaker(); }}
              className="btn-course-outline px-4 py-2.5 text-xs font-bold w-full flex items-center justify-center gap-1.5 mt-4">
              <PlusCircle size={12} /> Nieuw schema maken in Schema Maker
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function BestellingenTab({ token, onGoToMaker }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [localToast, setLocalToast] = useState(null);
  const [assignModal, setAssignModal] = useState(null);
  const [resendIntakeLoading, setResendIntakeLoading] = useState(null);
  const [customPayModal, setCustomPayModal] = useState(null);
  const [customPayLoading, setCustomPayLoading] = useState(false);
  const [mbLoading, setMbLoading] = useState(null);

  const authHeaders = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/orders`, { headers: authHeaders });
      const data = await res.json();
      setOrders(data.orders || []);
    } catch {}
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const deleteOrder = async (orderId) => {
    try {
      await fetch(`${API}/api/admin/orders/${orderId}`, { method: "DELETE", headers: authHeaders });
      setOrders(prev => prev.filter(o => o.order_id !== orderId));
      setDeleteConfirm(null);
      setLocalToast({ msg: "Bestelling verwijderd.", type: "success" });
    } catch { setLocalToast({ msg: "Verwijderen mislukt", type: "error" }); }
  };

  const [markeerLoading, setMarkeerLoading] = useState(null);
  const markeerBetaald = async (orderId) => {
    if (!window.confirm("Weet je zeker dat je deze bestelling handmatig als betaald wilt markeren? Schema en factuur worden automatisch aangemaakt.")) return;
    setMarkeerLoading(orderId);
    try {
      const res = await fetch(`${API}/api/admin/orders/${orderId}/markeer-betaald`, { method: "POST", headers: authHeaders });
      const data = await res.json();
      if (res.ok && data.ok) {
        setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, status: "betaald" } : o));
        setLocalToast({ msg: "Bestelling gemarkeerd als betaald. Schema en factuur aangemaakt!", type: "success" });
      } else {
        setLocalToast({ msg: data.detail || "Markeren mislukt", type: "error" });
      }
    } catch { setLocalToast({ msg: "Verbindingsfout", type: "error" }); }
    setMarkeerLoading(null);
  };

  const resendIntake = async (orderId) => {
    setResendIntakeLoading(orderId);
    try {
      const res = await fetch(`${API}/api/admin/orders/${orderId}/resend-intake`, { method: "POST", headers: authHeaders });
      if (res.ok) setLocalToast({ msg: "Intakeformulier opnieuw verstuurd!", type: "success" });
      else setLocalToast({ msg: "Versturen mislukt", type: "error" });
    } catch { setLocalToast({ msg: "Fout bij versturen", type: "error" }); }
    setResendIntakeLoading(null);
  };

  const [mbDeleteLoading, setMbDeleteLoading] = useState(null);

  const stuurMoneybirdFactuur = async (orderId) => {
    setMbLoading(orderId);
    try {
      const res = await fetch(`${API}/api/admin/orders/${orderId}/moneybird`, { method: "POST", headers: authHeaders });
      const data = await res.json();
      if (res.ok && data.ok) {
        setLocalToast({ msg: data.al_aangemaakt ? "Factuur bestaat al in Moneybird." : "Moneybird factuur aangemaakt en betaald!", type: "success" });
        setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, moneybird_invoice_id: data.moneybird_invoice_id } : o));
      } else {
        setLocalToast({ msg: `Moneybird fout: ${data.detail || "Onbekend"}`, type: "error" });
      }
    } catch { setLocalToast({ msg: "Verbindingsfout met Moneybird", type: "error" }); }
    setMbLoading(null);
  };

  const verwijderMoneybirdFactuur = async (orderId) => {
    if (!window.confirm("Weet je zeker dat je deze factuur wilt verwijderen uit Moneybird? Dit kan niet ongedaan worden gemaakt.")) return;
    setMbDeleteLoading(orderId);
    try {
      const res = await fetch(`${API}/api/admin/orders/${orderId}/moneybird`, { method: "DELETE", headers: authHeaders });
      const data = await res.json();
      if (res.ok && data.ok) {
        setLocalToast({ msg: "Factuur verwijderd uit Moneybird.", type: "success" });
        setOrders(prev => prev.map(o => o.order_id === orderId
          ? { ...o, moneybird_invoice_id: null, moneybird_contact_id: null } : o));
      } else {
        setLocalToast({ msg: `Fout: ${data.detail || data.bericht || "Onbekend"}`, type: "error" });
      }
    } catch { setLocalToast({ msg: "Verbindingsfout", type: "error" }); }
    setMbDeleteLoading(null);
  };

  const sendCustomPayment = async () => {
    if (!customPayModal?.bedrag || parseFloat(customPayModal.bedrag) <= 0) return;
    setCustomPayLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/orders/${customPayModal.order_id}/custom-payment`, {
        method: "POST", headers: authHeaders,
        body: JSON.stringify({ bedrag: parseFloat(customPayModal.bedrag), omschrijving: customPayModal.omschrijving || "Aangepaste betaling" }),
      });
      if (res.ok) {
        setLocalToast({ msg: `Betaallink verstuurd naar ${customPayModal.email}!`, type: "success" });
        setCustomPayModal(null);
      } else {
        const d = await res.json();
        setLocalToast({ msg: d.detail || "Versturen mislukt", type: "error" });
      }
    } catch { setLocalToast({ msg: "Fout bij aanmaken betaallink", type: "error" }); }
    setCustomPayLoading(false);
  };

  const downloadInvoice = (o) => {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const gold = [201, 168, 76], dark = [15, 15, 15], mid = [90, 90, 90], light = [220, 220, 220];
    const w = 210;
    doc.setFillColor(...dark); doc.rect(0, 0, w, 50, "F");
    doc.setFillColor(...gold); doc.rect(0, 0, 6, 50, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(22); doc.setTextColor(...gold);
    doc.text("FITSMITH", 18, 22);
    doc.setFontSize(9); doc.setTextColor(180, 180, 180);
    doc.text("Personal Training & Coaching", 18, 29);
    doc.setTextColor(100, 100, 100); doc.text("info@fitsmith.nl  ·  KvK: 42035017", 18, 36);
    doc.setFont("helvetica", "bold"); doc.setFontSize(28); doc.setTextColor(255, 255, 255);
    doc.text("FACTUUR", w - 20, 22, { align: "right" });
    doc.setFontSize(9); doc.setTextColor(120, 120, 120);
    doc.text(`Nr: ${o.order_id || "FS-XXXX"}`, w - 20, 30, { align: "right" });
    const datum = o.created_at ? new Date(o.created_at).toLocaleDateString("nl-NL", { day: "2-digit", month: "long", year: "numeric" }) : new Date().toLocaleDateString("nl-NL");
    doc.text(`Datum: ${datum}`, w - 20, 37, { align: "right" });
    let y = 62;
    doc.setFillColor(245, 245, 245); doc.roundedRect(15, y - 6, 85, 32, 2, 2, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...mid);
    doc.text("GEFACTUREERD AAN", 20, y); y += 6;
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...dark);
    doc.text(o.naam || "Onbekend", 20, y); y += 6;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...mid);
    doc.text(o.email || "", 20, y); y += 5;
    if (o.intake?.telefoon) doc.text(o.intake.telefoon, 20, y);
    const productLabel = o.product === "persoonlijk_schema"
      ? `Persoonlijk Trainingsschema${o.freq ? ` — ${o.freq} per week` : ""}`
      : o.product === "cursus" ? "FitSmith PT Kickstart Program"
      : o.product === "standaard_schema" ? "Standaard Schema"
      : o.product === "custom_schema" ? "Custom Trainingsschema"
      : o.product || "Product";
    y = 105;
    doc.setFillColor(...dark); doc.rect(15, y - 5, w - 30, 10, "F");
    doc.setFillColor(...gold); doc.rect(15, y - 5, 3, 10, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(255, 255, 255);
    doc.text("OMSCHRIJVING", 22, y + 1); doc.text("BEDRAG", w - 20, y + 1, { align: "right" });
    y += 15;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(...dark);
    doc.text(productLabel, 22, y);
    if (o.schema_label) { doc.setFontSize(8); doc.setTextColor(...mid); doc.text(o.schema_label, 22, y + 5); }
    const prijs = o.price != null ? o.price : (o.product === "cursus" ? 399.95 : 0);
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...dark);
    doc.text(prijs === 0 ? "Gratis" : `€ ${prijs.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, w - 20, y, { align: "right" });
    y += 18; doc.setDrawColor(...light); doc.setLineWidth(0.3); doc.line(15, y, w - 15, y);
    y += 10;
    // BTW niet van toepassing
    doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...mid);
    doc.text("BTW niet van toepassing (art. 25 Wet OB 1968)", 20, y);
    y += 8;
    doc.setFillColor(248, 248, 248); doc.roundedRect(w - 80, y - 6, 65, 22, 2, 2, "F");
    doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...mid);
    doc.text("TOTAAL", w - 20, y, { align: "right" }); y += 8;
    doc.setFont("helvetica", "bold"); doc.setFontSize(16); doc.setTextColor(...gold);
    doc.text(prijs === 0 ? "Gratis" : `€ ${prijs.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, w - 20, y, { align: "right" });
    y += 18; doc.setFont("helvetica", "bold"); doc.setFontSize(8);
    if (o.status === "schema_gereed" || o.status === "betaald") {
      doc.setTextColor(34, 197, 94); doc.text("✓ BETAALD / AFGEROND", 20, y);
    } else {
      doc.setTextColor(234, 179, 8); doc.text("⏳ IN BEHANDELING", 20, y);
    }
    const footerY = 270;
    doc.setFillColor(...dark); doc.rect(0, footerY, w, 27, "F");
    doc.setFillColor(...gold); doc.rect(0, footerY, w, 1.5, "F");
    doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(120, 120, 120);
    doc.text("FitSmith Personal Training  ·  info@fitsmith.nl  ·  KvK: 42035017", w / 2, footerY + 9, { align: "center" });
    doc.setTextColor(60, 60, 60);
    doc.text("Bedankt voor uw vertrouwen in FitSmith!", w / 2, footerY + 16, { align: "center" });
    doc.save(`Factuur-FitSmith-${o.order_id || "order"}.pdf`);
  };

  const doAction = async (orderId, action) => {
    setActionLoading(orderId + "_" + action);
    try {
      const res = await fetch(`${API}/api/admin/orders/${orderId}/${action}`, { method: "POST", headers: authHeaders });
      if (res.ok) {
        setLocalToast({
          msg: action === "confirm-payment"
            ? "Betaling bevestigd! Klant ontvangt intakelink via e-mail."
            : "Klant genotificeerd — schema is klaar! Toegangscode verstuurd.",
          type: "success"
        });
        load();
      } else { setLocalToast({ msg: "Actie mislukt", type: "error" }); }
    } catch { setLocalToast({ msg: "Fout bij actie", type: "error" }); }
    setActionLoading(null);
  };

  const DEFAULT_PRICES = { persoonlijk_schema: 0, cursus: 399.95, standaard_schema: 0, custom_schema: 0 };
  const stats = orders.reduce((acc, o) => {
    const type = o.product || "onbekend";
    const prijs = (o.price != null && o.price !== undefined) ? o.price : (DEFAULT_PRICES[type] || 0);
    acc.totaal++;
    acc.omzet += prijs;
    if (!acc.types[type]) acc.types[type] = { count: 0, omzet: 0 };
    acc.types[type].count++;
    acc.types[type].omzet += prijs;
    return acc;
  }, { totaal: 0, omzet: 0, types: {} });

  const fmt = (val) => `€${val.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading) return <div className="py-16 text-center text-zinc-500 text-sm"><RefreshCw size={16} className="animate-spin mx-auto mb-2" /> Laden...</div>;

  if (orders.length === 0) return (
    <div className="py-16 text-center">
      <ShoppingCart size={32} className="text-zinc-700 mx-auto mb-3" />
      <p className="text-zinc-500">Nog geen bestellingen ontvangen.</p>
    </div>
  );

  return (
    <div className="space-y-4">
      {localToast && <Toast msg={localToast.msg} type={localToast.type} onClose={() => setLocalToast(null)} />}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-[#111] border border-white/8 rounded-2xl p-4 col-span-1">
          <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest mb-1">Bestellingen</p>
          <p className="text-white text-3xl font-bold">{stats.totaal}</p>
          <p className="text-zinc-600 text-xs mt-1">totaal ontvangen</p>
        </div>
        <div className="bg-[#111] border border-[#C9A84C]/20 rounded-2xl p-4 col-span-1">
          <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest mb-1">Omzet</p>
          <p className="text-[#C9A84C] text-3xl font-bold">{fmt(stats.omzet)}</p>
          <p className="text-zinc-600 text-xs mt-1">totale waarde</p>
        </div>
        {Object.entries(stats.types).map(([type, data]) => (
          <div key={type} className="bg-[#111] border border-white/8 rounded-2xl p-4 col-span-1">
            <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest mb-1 truncate">
              {type === "persoonlijk_schema" ? "Pers. Schema" : type === "cursus" ? "Cursus" : type === "standaard_schema" ? "Stand. Schema" : type === "custom_schema" ? "Custom Schema" : type}
            </p>
            <p className="text-white text-2xl font-bold">{data.count}×</p>
            <p className="text-[#C9A84C] text-xs font-semibold mt-1">{fmt(data.omzet)}</p>
            <p className="text-zinc-700 text-xs">gemiddeld {fmt(data.count > 0 ? data.omzet / data.count : 0)}</p>
          </div>
        ))}
      </div>

      <div className="bg-[#0a0a0a] border border-white/5 rounded-xl px-5 py-4 mb-2">
        <p className="text-zinc-600 text-xs font-bold uppercase tracking-widest mb-3">Werkstroom</p>
        <div className="flex items-center gap-3 flex-wrap text-xs text-zinc-500">
          {[
            { color: "bg-yellow-400", label: "1. Betaling bevestigen" },
            { color: "bg-blue-400", label: "2. Klant vult intake in" },
            { color: "bg-purple-400", label: "3. Schema maken & toewijzen" },
            { color: "bg-green-400", label: "4. Schema gereed melden" },
          ].map(({ color, label }) => (
            <div key={label} className="flex items-center gap-1.5">
              <div className={`w-2 h-2 rounded-full ${color}`} />
              {label}
            </div>
          ))}
        </div>
      </div>

      {orders.map(o => {
        const sc = STATUS_CONFIG[o.status] || { label: o.status, cls: "text-zinc-400 bg-zinc-400/10 border-zinc-400/25" };
        const isOpen = expanded === o.order_id;
        return (
          <div key={o.order_id} className="bg-[#0f0f0f] border border-white/8 rounded-2xl overflow-hidden">
            <div className="p-5 flex flex-col sm:flex-row sm:items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <p className="text-white font-bold text-sm">{o.naam}</p>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${sc.cls}`}>{sc.label}</span>
                  {o.product && (
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                      o.product === "persoonlijk_schema" ? "text-purple-300 bg-purple-400/10 border-purple-400/25"
                      : o.product === "cursus" ? "text-blue-300 bg-blue-400/10 border-blue-400/25"
                      : o.product === "standaard_schema" ? "text-cyan-300 bg-cyan-400/10 border-cyan-400/25"
                      : o.product === "custom_schema" ? "text-emerald-300 bg-emerald-400/10 border-emerald-400/25"
                      : "text-zinc-400 bg-zinc-400/10 border-zinc-400/25"
                    }`}>
                      {o.product === "persoonlijk_schema" ? `Persoonlijk Schema${o.freq ? ` · ${o.freq}/wk` : ""}`
                        : o.product === "cursus" ? "Cursus"
                        : o.product === "standaard_schema" ? "Standaard Schema"
                        : o.product === "custom_schema" ? "Custom Schema"
                        : o.product}
                    </span>
                  )}
                  {(o.product === "standaard_schema" || o.product === "custom_schema") && o.schema_label && (
                    <span className="text-xs text-zinc-400 font-medium">{o.schema_label}</span>
                  )}
                  {o.handmatig && (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full border text-zinc-500 bg-zinc-500/10 border-zinc-500/25">Handmatig</span>
                  )}
                </div>
                <p className="text-zinc-500 text-xs">{o.email}</p>
                <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                  <p className="text-zinc-600 text-xs font-mono">{o.order_id} · {new Date(o.created_at).toLocaleDateString("nl-NL")}</p>
                  <span className="text-[#C9A84C] text-xs font-bold tracking-wide">
                    {(o.price != null ? o.price : (o.product === "cursus" ? 399.95 : 0)) === 0
                      ? <span className="text-green-400">Gratis</span>
                      : `€${(o.price != null ? o.price : (o.product === "cursus" ? 399.95 : 0)).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    }
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-2 shrink-0 items-stretch sm:items-end">
                {o.status === "aanvraag_ontvangen" && (
                  <button onClick={() => doAction(o.order_id, "confirm-payment")}
                    disabled={actionLoading === o.order_id + "_confirm-payment"}
                    data-testid={`confirm-payment-${o.order_id}`}
                    className="btn-gold-course px-4 py-2 text-xs font-bold uppercase tracking-wide flex items-center gap-1.5 disabled:opacity-60 justify-center">
                    <CheckCircle size={12} /> Betaling bevestigen
                  </button>
                )}
                {o.status === "intake_ontvangen" && (
                  <button onClick={() => setAssignModal({ order_id: o.order_id, user_id: o.user_id, naam: o.naam })}
                    data-testid={`assign-schema-order-${o.order_id}`}
                    className="btn-course-outline px-4 py-2 text-xs font-bold flex items-center gap-1.5 justify-center">
                    <Dumbbell size={12} /> Schema toewijzen
                  </button>
                )}
                {o.status === "intake_ontvangen" && (
                  <button onClick={() => doAction(o.order_id, "schema-ready")}
                    disabled={actionLoading === o.order_id + "_schema-ready"}
                    data-testid={`schema-ready-${o.order_id}`}
                    className="btn-gold-course px-4 py-2 text-xs font-bold uppercase tracking-wide flex items-center gap-1.5 disabled:opacity-60 justify-center">
                    <CheckCircle size={12} /> Schema gereed melden
                  </button>
                )}
                {/* Handmatig betaald markeren — voor niet-betaalde orders */}
                {!["betaald", "schema_gereed"].includes(o.status) && (
                  <button
                    onClick={() => markeerBetaald(o.order_id)}
                    disabled={markeerLoading === o.order_id}
                    data-testid={`markeer-betaald-${o.order_id}`}
                    className="px-4 py-2 rounded-xl border border-green-500/30 text-green-400 text-xs font-bold hover:bg-green-500/10 transition-all disabled:opacity-50 flex items-center gap-1.5 justify-center"
                  >
                    <CheckCircle size={12} /> {markeerLoading === o.order_id ? "Verwerken..." : "Markeer als betaald"}
                  </button>
                )}
                {o.intake && (
                  <button onClick={() => setExpanded(isOpen ? null : o.order_id)}
                    data-testid={`expand-order-${o.order_id}`}
                    className="btn-course-outline px-4 py-2 text-xs font-bold flex items-center gap-1.5 justify-center">
                    <Eye size={12} /> Intake {isOpen ? "verbergen" : "bekijken"}
                  </button>
                )}
                {o.status === "betaald" && !o.intake && (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-1.5 text-zinc-600 text-xs px-2">
                      <Clock size={11} /> Wacht op intake van klant
                    </div>
                    <button onClick={() => { const url = `${window.location.origin}/intake/${o.order_id}`; navigator.clipboard.writeText(url); }}
                      data-testid={`copy-intake-url-${o.order_id}`}
                      className="btn-course-outline px-4 py-2 text-xs font-bold flex items-center gap-1.5 justify-center">
                      <Copy size={11} /> Kopieer intakelink
                    </button>
                  </div>
                )}
                {(o.status === "betaald" || o.status === "intake_ontvangen") && (
                  <button onClick={() => resendIntake(o.order_id)}
                    disabled={resendIntakeLoading === o.order_id}
                    data-testid={`resend-intake-${o.order_id}`}
                    className="btn-course-outline px-4 py-2 text-xs font-bold flex items-center gap-1.5 justify-center">
                    <Mail size={11} /> {resendIntakeLoading === o.order_id ? "Bezig..." : "Intake opnieuw sturen"}
                  </button>
                )}
                <button onClick={() => stuurMoneybirdFactuur(o.order_id)}
                  disabled={mbLoading === o.order_id}
                  data-testid={`moneybird-btn-${o.order_id}`}
                  title={o.moneybird_invoice_id ? `Factuur: ${o.moneybird_invoice_id}` : "Aanmaken in Moneybird"}
                  className={`btn-course-outline px-4 py-2 text-xs font-bold flex items-center gap-1.5 justify-center ${o.moneybird_invoice_id ? "border-green-600/40 text-green-400" : ""}`}>
                  {mbLoading === o.order_id ? (
                    <span className="flex items-center gap-1.5"><div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" /> Bezig...</span>
                  ) : o.moneybird_invoice_id ? (
                    <span className="flex items-center gap-1.5"><CheckCircle size={11} /> Moneybird</span>
                  ) : (
                    <span className="flex items-center gap-1.5"><FileText size={11} /> Moneybird</span>
                  )}
                </button>
                {o.moneybird_invoice_id && (
                  <button onClick={() => verwijderMoneybirdFactuur(o.order_id)}
                    disabled={mbDeleteLoading === o.order_id}
                    data-testid={`moneybird-delete-btn-${o.order_id}`}
                    title="Verwijder factuur uit Moneybird"
                    className="btn-course-outline px-4 py-2 text-xs font-bold flex items-center gap-1.5 justify-center border-red-600/40 text-red-400 hover:border-red-500/60">
                    {mbDeleteLoading === o.order_id ? (
                      <span className="flex items-center gap-1.5"><div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" /> Bezig...</span>
                    ) : (
                      <span className="flex items-center gap-1.5"><Trash2 size={11} /> Verwijder factuur</span>
                    )}
                  </button>
                )}
                <button onClick={() => setCustomPayModal({ order_id: o.order_id, email: o.email, naam: o.naam, bedrag: "", omschrijving: "" })}
                  data-testid={`custom-pay-btn-${o.order_id}`}
                  className="btn-course-outline px-4 py-2 text-xs font-bold flex items-center gap-1.5 justify-center">
                  <Link size={11} /> Stuur betaallink
                </button>
                {o.status === "schema_gereed" && (
                  <div className="flex items-center gap-1.5 text-green-500 text-xs px-2">
                    <CheckCircle size={11} /> Afgerond
                  </div>
                )}
                <div className="flex items-center gap-1 self-end">
                  <button onClick={() => downloadInvoice(o)} title="Factuur downloaden als PDF"
                    data-testid={`download-invoice-btn-${o.order_id}`}
                    className="p-1.5 text-zinc-600 hover:text-[#C9A84C] transition-colors">
                    <Download size={14} />
                  </button>
                  <button onClick={() => setDeleteConfirm(o)}
                    data-testid={`delete-order-btn-${o.order_id}`}
                    className="p-1.5 text-zinc-700 hover:text-red-400 transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>

            {isOpen && o.intake && (
              <div className="border-t border-white/6 px-5 py-5 bg-[#0a0a0a]">
                <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-4">Intakeformulier</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {[
                    ["Naam", `${o.intake.voornaam} ${o.intake.achternaam}`],
                    ["Geboortedatum", o.intake.geboortedatum],
                    ["Geslacht", o.intake.geslacht],
                    ["Gewicht", o.intake.gewicht ? `${o.intake.gewicht} kg` : "–"],
                    ["Lengte", o.intake.lengte ? `${o.intake.lengte} cm` : "–"],
                    ["Doel", o.intake.doel],
                    ["Trainingsdagen", `${o.intake.trainingsdagen}x per week`],
                    ["Ervaring", o.intake.ervaring],
                    ["Klachten", o.intake.klachten || "Geen"],
                    ["Medicatie", o.intake.medicatie || "Geen"],
                    ["Sportschool", o.intake.sportschool || "–"],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <p className="text-zinc-600 text-xs uppercase tracking-wider mb-0.5">{k}</p>
                      <p className="text-white text-sm">{v || "–"}</p>
                    </div>
                  ))}
                </div>
                {o.intake.specifiek_doel && (
                  <div className="mt-4 p-3 bg-[#C9A84C]/8 border border-[#C9A84C]/20 rounded-xl">
                    <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-wider mb-1">Specifiek doel</p>
                    <p className="text-white text-sm leading-relaxed">{o.intake.specifiek_doel}</p>
                  </div>
                )}
                {o.intake.weekschema && (
                  <div className="mt-4">
                    <p className="text-zinc-600 text-xs uppercase tracking-wider mb-0.5">Weekschema</p>
                    <p className="text-white text-sm">{o.intake.weekschema}</p>
                  </div>
                )}
                {o.intake.mentaal && (
                  <div className="mt-3">
                    <p className="text-zinc-600 text-xs uppercase tracking-wider mb-0.5">Mentale aandachtspunten</p>
                    <p className="text-white text-sm">{o.intake.mentaal}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] border border-white/10 rounded-2xl p-6 max-w-sm w-full animate-fadeIn">
            <div className="w-12 h-12 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-4">
              <Trash2 size={20} className="text-red-400" />
            </div>
            <h3 className="text-white font-bold text-center mb-1">Bestelling verwijderen?</h3>
            <p className="text-zinc-500 text-sm text-center mb-5">
              <span className="text-white font-semibold">{deleteConfirm.naam}</span> ({deleteConfirm.order_id})<br />
              Dit kan niet ongedaan worden gemaakt.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="btn-course-outline flex-1 py-2.5 text-sm font-semibold">Annuleren</button>
              <button onClick={() => deleteOrder(deleteConfirm.order_id)} data-testid="confirm-delete-order-btn"
                className="flex-1 py-2.5 text-sm font-bold bg-red-500/15 border border-red-500/30 text-red-400 rounded-xl hover:bg-red-500/25 transition-colors">
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      {assignModal && (
        <SchemaToevoegenModal
          token={token}
          userId={assignModal.user_id}
          orderNaam={assignModal.naam}
          onClose={() => setAssignModal(null)}
          onAssigned={load}
          onGoToMaker={onGoToMaker}
        />
      )}

      {/* Betaallink modal */}
      {customPayModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setCustomPayModal(null)}>
          <div className="bg-[#111] border border-white/10 rounded-2xl p-7 max-w-sm w-full animate-fadeIn" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-white font-bold text-lg">Betaallink sturen</h3>
                <p className="text-zinc-500 text-xs mt-0.5 truncate">Naar: {customPayModal.naam} ({customPayModal.email})</p>
              </div>
              <button onClick={() => setCustomPayModal(null)} className="text-zinc-500 hover:text-white transition-colors"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-zinc-500 text-xs font-bold uppercase tracking-widest block mb-2">Bedrag (€)</label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400 font-bold text-lg">€</span>
                  <input
                    type="number" step="0.01" min="0.01"
                    value={customPayModal.bedrag}
                    onChange={e => setCustomPayModal(prev => ({ ...prev, bedrag: e.target.value }))}
                    placeholder="0,00"
                    data-testid="custom-pay-amount"
                    className="w-full bg-[#0f0f0f] border border-white/8 rounded-xl pl-9 pr-4 py-3 text-white text-xl font-bold focus:outline-none focus:border-[#C9A84C]/50 transition-colors"
                    autoFocus
                  />
                </div>
              </div>
              <div>
                <label className="text-zinc-500 text-xs font-bold uppercase tracking-widest block mb-2">Omschrijving</label>
                <input
                  type="text"
                  value={customPayModal.omschrijving}
                  onChange={e => setCustomPayModal(prev => ({ ...prev, omschrijving: e.target.value }))}
                  placeholder="bijv. Extra sessie, upsell..."
                  data-testid="custom-pay-desc"
                  className="w-full bg-[#0f0f0f] border border-white/8 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50 transition-colors"
                />
              </div>
              {customPayModal.bedrag && parseFloat(customPayModal.bedrag) > 0 && (
                <div className="bg-[#C9A84C]/6 border border-[#C9A84C]/20 rounded-xl px-4 py-3">
                  <p className="text-zinc-500 text-xs mb-1">Klant ontvangt betaallink voor:</p>
                  <p className="text-[#C9A84C] font-bold text-2xl">
                    € {parseFloat(customPayModal.bedrag).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              )}
              <button
                onClick={sendCustomPayment}
                disabled={customPayLoading || !customPayModal.bedrag || parseFloat(customPayModal.bedrag) <= 0}
                data-testid="custom-pay-submit"
                className="btn-gold-course w-full py-3 text-sm font-bold uppercase tracking-wide flex items-center justify-center gap-2 disabled:opacity-40 transition-opacity"
              >
                {customPayLoading ? "Betaallink aanmaken..." : <><Link size={13} /> Betaallink versturen</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
